import { useState, useEffect, useCallback } from 'react';
import { FavoriteFolder } from '@/types';

const STORAGE_KEY_USER_ACTIONS = 'vx_user_actions';

interface UserActions {
  favorites: string[]; // Tutorial IDs
  history: { tutorialId: string; timestamp: number }[];
  customFolders: FavoriteFolder[];
}

const DEFAULT_ACTIONS: UserActions = {
  favorites: [],
  history: [],
  customFolders: [],
};

export function useUserActions() {
  const [actions, setActions] = useState<UserActions>(DEFAULT_ACTIONS);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY_USER_ACTIONS);
    if (saved) {
      setActions(JSON.parse(saved));
    }
  }, []);

  const saveActions = useCallback((newActions: UserActions) => {
    setActions(newActions);
    localStorage.setItem(STORAGE_KEY_USER_ACTIONS, JSON.stringify(newActions));
  }, []);

  const toggleFavorite = useCallback((tutorialId: string) => {
    const newFavorites = actions.favorites.includes(tutorialId)
      ? actions.favorites.filter((id) => id !== tutorialId)
      : [...actions.favorites, tutorialId];
    
    saveActions({ ...actions, favorites: newFavorites });
  }, [actions, saveActions]);

  const addHistory = useCallback((tutorialId: string) => {
    const now = Date.now();
    const newHistory = [
      { tutorialId, timestamp: now },
      ...actions.history.filter((h) => h.tutorialId !== tutorialId),
    ].slice(0, 50); // Keep last 50

    saveActions({ ...actions, history: newHistory });
  }, [actions, saveActions]);

  const clearHistory = useCallback(() => {
    saveActions({ ...actions, history: [] });
  }, [actions, saveActions]);

  const createFolder = useCallback((name: string) => {
    if (actions.customFolders.length >= 20) return false;
    const newFolder: FavoriteFolder = {
      id: Math.random().toString(36).substr(2, 9),
      name: name.slice(0, 20),
      tutorialIds: [],
    };
    saveActions({ ...actions, customFolders: [...actions.customFolders, newFolder] });
    return true;
  }, [actions, saveActions]);

  const deleteFolder = useCallback((folderId: string) => {
    saveActions({
      ...actions,
      customFolders: actions.customFolders.filter((f) => f.id !== folderId),
    });
  }, [actions, saveActions]);

  const toggleTutorialInFolder = useCallback((folderId: string, tutorialId: string) => {
    const newFolders = actions.customFolders.map((folder) => {
      if (folder.id === folderId) {
        const tutorialIds = folder.tutorialIds.includes(tutorialId)
          ? folder.tutorialIds.filter((id) => id !== tutorialId)
          : [...folder.tutorialIds, tutorialId];
        return { ...folder, tutorialIds };
      }
      return folder;
    });
    saveActions({ ...actions, customFolders: newFolders });
  }, [actions, saveActions]);

  return {
    ...actions,
    toggleFavorite,
    addHistory,
    clearHistory,
    createFolder,
    deleteFolder,
    toggleTutorialInFolder,
  };
}
